import { useToast as usePrimeToast } from 'primevue/usetoast'

export type ToastSeverity = 'success' | 'info' | 'warn' | 'error'

export interface ToastOptions {
  summary?: string
  detail?: string
  life?: number
  closable?: boolean
  group?: string
}

/**
 * Composable for showing toast notifications
 *
 * Usage:
 * ```ts
 * const { success, error, info, warn } = useToast()
 *
 * success('Route saved!')
 * error('Failed to generate route', { detail: 'Please try again' })
 * ```
 */
export function useToast() {
  const toast = usePrimeToast()

  const show = (severity: ToastSeverity, message: string, options: ToastOptions = {}) => {
    toast.add({
      severity,
      summary: options.summary || getSummaryForSeverity(severity),
      detail: message,
      life: options.life ?? getLifeForSeverity(severity),
      closable: options.closable ?? true,
      group: options.group,
    })
  }

  const success = (message: string, options: ToastOptions = {}) => {
    show('success', message, { summary: 'Success', ...options })
  }

  const error = (message: string, options: ToastOptions = {}) => {
    show('error', message, { summary: 'Error', ...options })
  }

  const info = (message: string, options: ToastOptions = {}) => {
    show('info', message, { summary: 'Info', ...options })
  }

  const warn = (message: string, options: ToastOptions = {}) => {
    show('warn', message, { summary: 'Warning', ...options })
  }

  const clear = (group?: string) => {
    if (group) {
      toast.removeGroup(group)
    } else {
      toast.removeAllGroups()
    }
  }

  return {
    show,
    success,
    error,
    info,
    warn,
    clear,
  }
}

function getSummaryForSeverity(severity: ToastSeverity): string {
  const summaries: Record<ToastSeverity, string> = {
    success: 'Success',
    info: 'Info',
    warn: 'Warning',
    error: 'Error',
  }
  return summaries[severity]
}

function getLifeForSeverity(severity: ToastSeverity): number {
  const lifetimes: Record<ToastSeverity, number> = {
    success: 3000,
    info: 3000,
    warn: 3000,
    error: 3000,
  }
  return lifetimes[severity]
}

export default useToast
